import {
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { InputSize } from '../../../enum/input-size.enum';
import {
  NgxFloatUiContentComponent,
  NgxFloatUiDirective,
  NgxFloatUiPlacements,
  NgxFloatUiTriggers,
} from 'ngx-float-ui';
import { DatePickerComponent, DatePickerResult } from '../../../date-picker/date-picker.component';
import { MaskitoOptions } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { DateUtils } from '../../../../utils/date-utils';
import { disableDebugTools } from '@angular/platform-browser';

@Component({
  selector: 'lib-date-input',
  imports: [MatIcon, NgxFloatUiContentComponent, NgxFloatUiDirective, DatePickerComponent, MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateInputComponent implements ControlValueAccessor {
  placeholder = input<string>('');
  controlId = input<string | null>(null);
  error = input<boolean>(false);
  inputSize = input<InputSize>(InputSize.large);
  readonly minDate = input<string | undefined>();
  readonly maxDate = input<string | undefined>();
  readonly useFutureYearOptions = input<boolean>(false);
  valueChange = output<Date | null>();

  datePickerFloatUI = viewChild<NgxFloatUiContentComponent>('datePicker');

  public maskitoOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  };

  currentSelectedDate = signal<Date | null>(null);
  hasFocus = signal<boolean>(false);
  datePickerVisible = signal<boolean>(false);

  focusEffect = effect(() => {
    const selectedDate = this.currentSelectedDate();
    if (selectedDate === null) {
      return;
    }
    this.setInputValueStringFromDate(selectedDate);
  });

  public inputStringValue = signal<string | null>(null);
  public disabled = signal(false);

  protected readonly InputSize = InputSize;
  protected readonly NgxFloatUiTriggers = NgxFloatUiTriggers;
  protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
  protected readonly disableDebugTools = disableDebugTools;

  public writeValue(dateValue: Date | null): void {
    this.currentSelectedDate.set(dateValue);
    this.setInputValueStringFromDate(dateValue);
  }

  public registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public onFocus() {
    this.hasFocus.set(true);
  }

  public onBlur() {
    this.hasFocus.set(false);
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const dateFromInput = this.parseDateInputString(inputValue);
    // this.value = inputValue;
    this.currentSelectedDate.set(dateFromInput);
    this.valueChange.emit(dateFromInput);
    this.onChange(dateFromInput);
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public handleDatePickerResult(result: DatePickerResult) {
    this.currentSelectedDate.set(result.date);
    this.inputStringValue.set(DateUtils.dateToFullDateString(result.date));
    this.valueChange.emit(result.date);
    this.onChange(result.date);
  }

  onDatePickerVisibilityChange(visible: boolean) {
    this.datePickerVisible.set(visible);
  }

  private onChange: (value: Date | null) => void = () => {
    // default onChange
  };

  private onTouched: () => void = () => {
    // default onTouched
  };

  private setInputValueStringFromDate(dateValue: Date | null) {
    if (this.hasFocus() || this.datePickerVisible()) {
      this.inputStringValue.set(DateUtils.dateToInputDateString(dateValue, true));
    } else {
      this.inputStringValue.set(DateUtils.dateToFullDateString(dateValue, true));
    }
  }
  private parseDateInputString(inputString: string | null): Date | null {
    if (!inputString) {
      return null;
    } else {
      const dateFormat = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!dateFormat.test(inputString)) {
        return null;
      }

      const [day, month, year] = inputString.split('/').map(Number);

      if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
      }

      const date = new Date(Date.UTC(year, month - 1, day));
      if (isNaN(date.getTime())) {
        return null;
      }

      return date;
    }
  }
}
